import "./CandidateProfileEditor.css";
import { regionArray } from "../../utils/geographicSectors";
import PostalCityField from "../../components/PostalCityField";
import { usePostalCities } from "../../hooks/usePostalCities";
import { formatPhone } from "../../utils/formatPhone";
import SalaryCalculator from "./SalaryCalculator";
import { useState } from "react";
import axios from "../../axios/AxiosInstance";
import { message, Select } from "antd";
import { candidateCompetenceCatalogs as catalogs } from "../../data/candidateCompetenceCatalogs";
import { countries } from "../../utils/countries";

const civilities = [
  { value: "MR", label: "Monsieur" },
  { value: "MME", label: "Madame" },
];
const fieldSuggestions = {
  bankName: [
    "BNP Paribas",
    "Crédit Agricole",
    "Crédit Mutuel",
    "CIC",
    "Société Générale",
    "La Banque Postale",
    "Caisse d’Épargne",
    "Banque Populaire",
    "BoursoBank",
    "Revolut",
    "N26",
  ],
};
const fields = [
  [
    "Coordonnées",
    [
      ["firstname", "Prénom"],
      ["lastname", "Nom"],
      ["email", "Email", "email"],
      ["phone", "Téléphone principal", "tel"],
      ["phone2", "Téléphone secondaire", "tel"],
      ["address", "Adresse"],
      ["town", "Ville"],
      ["postCode", "Code postal"],
    ],
  ],
  [
    "Informations personnelles",
    [
      ["civility", "Civilité"],
      ["dob", "Date de naissance", "date"],
      ["nationality", "Nationalité"],
      ["birthCountry", "Pays de naissance"],
      ["birthTown", "Ville de naissance"],
      ["birthDepartment", "Département de naissance"],
      ["securityNumber", "Numéro de sécurité sociale"],
      ["pld", "Numéro PLD", "readonly"],
    ],
  ],
  [
    "Coordonnées bancaires",
    [
      ["iban", "IBAN"],
      ["bic", "BIC"],
      ["bankName", "Banque"],
    ],
  ],
  [
    "Contact en cas d’urgence",
    [
      ["urgentContactName", "Nom du contact"],
      ["urgentContactPhone", "Téléphone du contact", "tel"],
    ],
  ],
];
const sectionColors = {
  Coordonnées: "border-blue-200 bg-blue-50 text-blue-900",
  "Informations personnelles": "border-pink-200 bg-pink-50 text-pink-900",
  "Prétentions salariales (brut)":
    "border-emerald-200 bg-emerald-50 text-emerald-900",
  "Prétentions salariales (net)": "border-cyan-200 bg-cyan-50 text-cyan-900",
  "Coordonnées bancaires": "border-purple-200 bg-purple-50 text-purple-900",
  "Contact en cas d’urgence": "border-amber-200 bg-amber-50 text-amber-900",
};
const competenceFields = [
  ["qualifications", "Qualifications / métiers recherchés"],
  ["diplomas", "Diplômes"],
  ["habilitations", "Habilitations"],
  ["caceses", "CACES"],
  ["licences", "Permis de conduire"],
];
const labels = (value) =>
  (Array.isArray(value) ? value : value ? [value] : [])
    .map((item) =>
      typeof item === "string" ? item : item?.name || item?.label || "",
    )
    .filter(Boolean);
const withCurrent = (catalog, current) => [
  ...new Set([...(catalog || []), ...labels(current)]),
];
const profileForm = (candidate) => ({
  ...candidate,
  civility: candidate.title || candidate.civility || "",
  dob: candidate.dob ? String(candidate.dob).slice(0, 10) : "",
});
export default function CandidateProfileEditor({ candidate, onUpdated }) {
  const savedJobs = [
    ...new Set(
      (Array.isArray(candidate.qualifications)
        ? candidate.qualifications
        : candidate.qualification
          ? [candidate.qualification]
          : []
      )
        .map((job) =>
          typeof job === "string" ? job : job?.name || job?.label || "",
        )
        .filter(Boolean),
    ),
  ];
  const [nationalitySearch, setNationalitySearch] = useState("");
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(profileForm(candidate));
  const [busy, setBusy] = useState(false);
  const postal = usePostalCities({
    postCode: form.postCode,
    town: form.town,
    enabled: editing,
    onTownChange: (town) => setForm((current) => ({ ...current, town })),
  });
  const save = async (event) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const payload = Object.fromEntries(
        fields.flatMap(([, list]) =>
          list
            .filter(([, , type]) => type !== "readonly")
            .map(([key]) => [key, form[key] ?? ""]),
        ),
      );
      competenceFields.forEach(([key]) => {
        if (form[key] !== undefined) payload[key] = form[key];
      });
      payload.locations = labels(form.locations || form.zones);
      payload.title = payload.civility;
      delete payload.civility;
      const { data } = await axios.post(
        `tempworker-candidate/${candidate._id}/update`,
        payload,
      );
      if (!data?._id) throw new Error("Enregistrement non confirmé");
      onUpdated({ ...candidate, ...data });
      setEditing(false);
      message.success("Profil enregistré.");
    } catch {
      message.error(
        "Impossible d’enregistrer le profil. Vos saisies sont conservées.",
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={save} className="candidate-profile-information space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-2xl font-bold text-gray-900">
            Informations du candidat
          </h3>
          <p className="mt-1 text-sm text-slate-500">
            Coordonnées, métier recherché et personne à prévenir.
          </p>
        </div>
        {editing && <button type="submit" disabled={busy} className="rounded-xl bg-blue-700 px-4 py-2 text-base font-semibold text-white disabled:opacity-50">{busy ? "Enregistrement…" : "Enregistrer les informations"}</button>}
        {!editing && (
          <button
            type="button"
            onClick={() => {
              setForm(profileForm(candidate));
              setEditing(true);
            }}
            className="rounded-xl bg-blue-700 px-4 py-2 text-sm font-semibold text-white"
          >
            Modifier les informations
          </button>
        )}
      </div>
      <div className="grid items-start gap-3 lg:grid-cols-2">
        {fields.map(([title, list]) => (
          <section
            key={title}
            className="min-w-0 rounded-xl border border-slate-200 bg-white p-4"
          >
            <h4
              className={`-mx-4 -mt-4 mb-3 rounded-t-xl border-b px-4 py-3 text-sm font-semibold ${sectionColors[title] || "border-slate-200 bg-slate-50 text-slate-900"}`}
            >
              {title}
            </h4>
            {title === "Métier recherché" &&
              editing &&
              savedJobs.length > 0 && (
                <div className="mb-3 flex flex-wrap gap-2">
                  {savedJobs.map((job) => (
                    <span
                      key={job}
                      className="rounded-lg bg-blue-50 px-2 py-1 text-sm text-blue-900"
                    >
                      {job}
                    </span>
                  ))}
                </div>
              )}
            <div className="grid gap-x-5 gap-y-3 sm:grid-cols-2">
              {list.map(([key, label, type = "text"]) => (
                <div key={key}>
                  <label
                    htmlFor={`profile-${key}`}
                    className="text-xs font-medium text-slate-500"
                  >
                    {label}
                  </label>
                  {editing && key === "civility" ? (
                    <select
                      id={`profile-${key}`}
                      value={form[key] || ""}
                      onChange={(e) =>
                        setForm({ ...form, [key]: e.target.value })
                      }
                      className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                    >
                      <option value="">Choisir une civilité</option>
                      {form[key] &&
                        !civilities.some(
                          (option) => option.value === form[key],
                        ) && <option value={form[key]}>{form[key]}</option>}
                      {civilities.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  ) : editing && key === "town" ? (
                    <PostalCityField
                      id="profile-town"
                      value={form.town}
                      postCode={form.postCode}
                      cities={postal.cities}
                      onChange={(town) =>
                        setForm((current) => ({ ...current, town }))
                      }
                      className="mt-1"
                    />
                  ) : editing && key === "nationality" ? (
                    <Select
                      aria-label="Nationalité"
                      showSearch
                      className="mt-1 w-full"
                      getPopupContainer={(trigger) => trigger.parentElement}
                      value={form.nationality || undefined}
                      placeholder="Choisir une nationalité"
                      options={withCurrent(countries, form.nationality).map(
                        (value) => ({ value, label: value }),
                      )}
                      onSearch={(value) => setNationalitySearch(value)}
                      onChange={(value) =>
                        setForm({ ...form, nationality: value })
                      }
                      dropdownRender={(menu) => (
                        <>
                          {menu}
                          {nationalitySearch &&
                            !countries.includes(nationalitySearch) && (
                              <button
                                type="button"
                                className="w-full px-3 py-2 text-left text-sm text-blue-800"
                                onClick={() => {
                                  setForm({
                                    ...form,
                                    nationality: nationalitySearch.trim(),
                                  });
                                  setNationalitySearch("");
                                }}
                              >
                                Utiliser « {nationalitySearch} »
                              </button>
                            )}
                        </>
                      )}
                    />
                  ) : editing && type !== "readonly" ? (
                    <input
                      id={`profile-${key}`}
                      list={
                        fieldSuggestions[key] ? `suggestions-${key}` : undefined
                      }
                      type={type}
                      step={type === "number" ? "0.01" : undefined}
                      min={type === "number" ? 0 : undefined}
                      required={["firstname", "lastname"].includes(key)}
                      value={
                        type === "date"
                          ? String(form[key] || "").slice(0, 10)
                          : (form[key] ?? "")
                      }
                      onChange={(e) =>
                        setForm({ ...form, [key]: e.target.value })
                      }
                      className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                    />
                  ) : key === "jobTitle" && savedJobs.length ? (
                    <div className="mt-1 flex flex-wrap gap-2">
                      {savedJobs.map((job) => (
                        <span
                          key={job}
                          className="rounded-lg bg-blue-50 px-2 py-1 text-sm text-blue-900"
                        >
                          {job}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="break-words text-sm text-slate-900">
                      {type === "date" && candidate[key]
                        ? new Date(candidate[key]).toLocaleDateString("fr-FR", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                          })
                        : type === "tel"
                          ? formatPhone(candidate[key]) || "—"
                          : key === "civility"
                            ? civilities.find(
                                (option) =>
                                  option.value ===
                                  (candidate.title || candidate.civility),
                              )?.label ||
                              candidate.title ||
                              candidate.civility ||
                              "—"
                            : candidate[key] || "—"}
                    </p>
                  )}
                  {fieldSuggestions[key] && (
                    <datalist id={`suggestions-${key}`}>
                      {fieldSuggestions[key].map((value) => (
                        <option key={value} value={value} />
                      ))}
                    </datalist>
                  )}
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
      <section className="rounded-xl border border-slate-200 bg-white p-4">
        <h4 className="-mx-4 -mt-4 mb-3 rounded-t-xl border-b border-blue-200 bg-blue-50 px-4 py-3 text-sm font-semibold text-blue-900">
          Métiers recherchés et autres compétences
        </h4>
        <div className="mb-4">
          <label className="mb-2 block text-xs font-medium text-slate-500">
            Zone géographique recherchée
          </label>
          {editing ? (
            <Select
              aria-label="Zone géographique recherchée"
              mode="multiple"
              showSearch
              optionFilterProp="label"
              className="w-full"
              getPopupContainer={(trigger) => trigger.parentElement}
              value={labels(form.locations || form.zones)}
              onChange={(locations) => setForm({ ...form, locations })}
              placeholder="Choisir les départements de mobilité"
              options={withCurrent(
                regionArray,
                form.locations || form.zones,
              ).map((value) => ({ value, label: value }))}
            />
          ) : (
            <p className="text-sm text-slate-900">
              {labels(candidate.locations || candidate.zones).join(", ") ||
                "Non renseignée"}
            </p>
          )}
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {competenceFields.map(([key, label]) => (
            <div key={key}>
              <label className="mb-2 block text-xs font-medium text-slate-500">
                {label}
              </label>
              {editing ? (
                <Select
                  aria-label={label}
                  mode="multiple"
                  showSearch
                  optionFilterProp="label"
                  className="w-full"
                  getPopupContainer={(trigger) => trigger.parentElement}
                  value={labels(
                    form[key] ||
                      (key === "qualifications"
                        ? candidate.qualification || candidate.jobTitle
                        : []),
                  )}
                  onChange={(values) => setForm({ ...form, [key]: values })}
                  placeholder="Choisir"
                  options={withCurrent(
                    catalogs[key],
                    form[key] ||
                      (key === "qualifications"
                        ? candidate.qualification || candidate.jobTitle
                        : candidate[key]),
                  ).map((value) => ({ value, label: value }))}
                />
              ) : (
                <div className="flex flex-wrap gap-2">
                  {labels(
                    candidate[key] ||
                      (key === "qualifications"
                        ? candidate.qualification || candidate.jobTitle
                        : []),
                  ).length ? (
                    labels(
                      candidate[key] ||
                        (key === "qualifications"
                          ? candidate.qualification || candidate.jobTitle
                          : []),
                    ).map((value) => (
                      <span
                        key={value}
                        className="rounded-lg bg-blue-50 px-2 py-1 text-sm text-blue-900"
                      >
                        {value}
                      </span>
                    ))
                  ) : (
                    <span className="text-sm text-slate-500">—</span>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>
      <SalaryCalculator
        key={candidate._id}
        candidate={candidate}

      />
      {editing && (
        <div className="sticky bottom-0 flex justify-end gap-3 rounded-xl border border-slate-200 bg-white p-4">
          <button
            type="button"
            disabled={busy}
            onClick={() => setEditing(false)}
            className="rounded-lg border px-4 py-2 text-sm"
          >
            Annuler
          </button>
          <button
            disabled={busy}
            className="rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white"
          >
            {busy ? "Enregistrement…" : "Enregistrer les informations"}
          </button>
        </div>
      )}
    </form>
  );
}
